// REVIEW CENTER -> PARTNER IMPORT (owner, 2026-10-15: "show a UI to validate
// edit or map exitisting partners to newly imported partners in the review
// center").
//
// One import at a time (Partners > Import partner documentation), one card
// per proposed partner:
//   - Add as a new partner / Map to an existing one (the suggestions first,
//     each with its confidence and WHY) / Reject;
//   - the company name and trades, editable;
//   - per person: include or not, name, email, role (supervisor / employee /
//     executive), the account and roster entries their address already has;
//   - where every value came from: file, page, the line, and whether it was
//     found by pattern, by document reading, or both.
// Preview shows the plan the server recomputes - who goes where, what is
// skipped and why - and Apply writes exactly that. Nothing is emailed. An
// applied import shows what it did, with Undo.
//
// The server holds the rules (routes/partnerImport): a reviewer who may not
// set supervisors gets those people as employees, executives skipped, and
// the result says so; this page shows that warning up front rather than
// letting the toggle look like it worked.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Card, Checkbox, Empty, Input, Modal, Popover, Radio, Select, Space, Spin, Table, Tag, Tooltip, message,
} from "antd";
import { CheckOutlined, EyeOutlined, RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import {
  listPartnerImports, getPartnerImport, savePartnerImportDecisions, previewPartnerImport, applyPartnerImport, undoPartnerImport,
} from "api/participants/partnerImportApi";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#667085", line: "#EFEFEF" };
const METHOD = { pattern: "pattern", reading: "document reading" };
const methods = (list) => (list || []).map((m) => METHOD[m] || m).join(" + ");
const ROLE_OPTIONS = [
  { value: "supervisor", label: "Supervisor" },
  { value: "employee", label: "Employee" },
  { value: "executive", label: "Executive (reports only)" },
];

// The reviewer's working copy of one proposal: the saved decision, or the
// suggestion the job made.
function initialDecision(p) {
  const d = p.decision || {};
  const byId = new Map((d.people || []).map((x) => [x.id, x]));
  return {
    action: d.action || (p.suggested && p.suggested.action) || "create",
    companyId: d.companyId || (p.suggested && p.suggested.companyId) || undefined,
    companyName: d.companyName !== undefined ? d.companyName : (p.companyName || ""),
    trades: Array.isArray(d.trades) ? d.trades : (p.trades || []),
    people: (p.people || []).map((x) => ({
      id: x.id,
      include: byId.has(x.id) && byId.get(x.id).include !== undefined ? !!byId.get(x.id).include : !!x.include,
      name: byId.has(x.id) && byId.get(x.id).name !== undefined ? byId.get(x.id).name : x.name,
      email: byId.has(x.id) && byId.get(x.id).email !== undefined ? byId.get(x.id).email : x.email,
      role: (byId.get(x.id) || {}).role || x.role || "employee",
      jobTitle: (byId.get(x.id) || {}).jobTitle !== undefined ? byId.get(x.id).jobTitle : (x.jobTitle || ""),
    })),
  };
}

function Sources({ list }) {
  if (!list || !list.length) return null;
  return (
    <Popover
      title="Where this was found"
      content={(
        <div style={{ maxWidth: 420, maxHeight: 280, overflow: "auto" }}>
          {list.map((s, i) => (
            <div key={i} style={{ fontSize: 12, marginBottom: 6 }}>
              <div><b>{s.file}</b> · page {s.page} · {METHOD[s.method] || s.method}{s.field ? ` · ${s.field}` : ""}</div>
              {s.snippet && <div style={{ color: T.muted, fontFamily: "monospace" }}>{s.snippet}</div>}
            </div>
          ))}
        </div>
      )}
    >
      <Button type="link" size="small" style={{ padding: 0 }}>sources ({list.length})</Button>
    </Popover>
  );
}

function ProposalCard({ p, d, onChange, tradeOptions, partnerOrgs, can }) {
  const set = (patch) => onChange({ ...d, ...patch });
  const setPerson = (id, patch) => onChange({ ...d, people: d.people.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const mapOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    // Each company with its trades beneath (owner, 2026-10-16), from the
    // partner organisations list; searched by name and trade.
    const tradesOf = (id) => (((partnerOrgs || []).find((o) => o.companyId === id) || {}).trades || []);
    const opt = (value, text, title) => {
      const t = tradesOf(value);
      return { value, text, title, search: `${text} ${t.join(" ")}`, label: <PartnerWithTrades name={text} trades={t} /> };
    };
    (p.matches || []).forEach((m) => {
      seen.add(m.companyId);
      out.push(opt(m.companyId, `${m.companyName} - ${Math.round(m.confidence * 100)}%${m.onThisProject ? " (on this project)" : ""}`, (m.why || []).join("; ")));
    });
    (partnerOrgs || []).forEach((o) => {
      if (!seen.has(o.companyId)) out.push(opt(o.companyId, `${o.name}${o.onThisProject ? " (on this project)" : ""}`, `${o.people} people on your projects`));
    });
    return out;
  }, [p, partnerOrgs]);
  const best = (p.matches || [])[0];
  const columns = [
    {
      title: "", dataIndex: "include", width: 32,
      render: (_, x) => <Checkbox checked={x.include} onChange={(e) => setPerson(x.id, { include: e.target.checked })} aria-label={`include ${x.name}`} />,
    },
    {
      title: "Name", dataIndex: "name",
      render: (_, x) => {
        const src = (p.people || []).find((y) => y.id === x.id) || {};
        return (
          <Tooltip title={src.found && src.found.name ? `Found by ${methods(src.found.name)}` : ""}>
            <Input size="small" value={x.name} onChange={(e) => setPerson(x.id, { name: e.target.value })} />
          </Tooltip>
        );
      },
    },
    {
      title: "Email", dataIndex: "email",
      render: (_, x) => {
        const src = (p.people || []).find((y) => y.id === x.id) || {};
        return (
          <Tooltip title={src.found && src.found.email ? `Found by ${methods(src.found.email)}` : "No address found - a roster entry needs one"}>
            <Input size="small" value={x.email} status={x.include && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.email || "") ? "error" : ""} onChange={(e) => setPerson(x.id, { email: e.target.value })} />
          </Tooltip>
        );
      },
    },
    {
      title: "Role", dataIndex: "role", width: 190,
      render: (_, x) => (
        <Select size="small" style={{ width: "100%" }} value={x.role} options={ROLE_OPTIONS} onChange={(v) => setPerson(x.id, { role: v })} />
      ),
    },
    {
      title: "Already known", key: "known",
      render: (_, x) => {
        const src = (p.people || []).find((y) => y.id === x.id) || {};
        return (
          <Space size={4} wrap>
            {src.jobTitle && <Tag>{src.jobTitle}</Tag>}
            {src.phone && <span style={{ fontSize: 12, color: T.muted }}>{src.phone}</span>}
            {src.account && <Tag color="blue">{`account: ${src.account.name || "yes"} (${src.account.companyName || "-"})`}</Tag>}
            {src.onThisProject && <Tag color="orange">{`on ${src.onThisProject.companyName} here`}</Tag>}
            {(src.roster || []).filter((r) => !r.here).slice(0, 2).map((r, i) => <Tag key={i}>{`${r.companyName} on ${r.projectTitle}`}</Tag>)}
            {(src.alsoUnder || []).length > 0 && <Tag color="red">{`also listed under ${src.alsoUnder.join(", ")}`}</Tag>}
            <Sources list={src.sources} />
          </Space>
        );
      },
    },
  ];
  return (
    <Card
      size="small"
      data-testid={`proposal-${p.id}`}
      style={{ marginBottom: 12, borderColor: d.action === "reject" ? "#f0f0f0" : undefined, opacity: d.action === "reject" ? 0.6 : 1 }}
      title={(
        <Space wrap>
          <span>{p.companyName || "Contacts with no company"}</span>
          {(p.domains || []).map((dm) => <Tag key={dm}>@{dm}</Tag>)}
          {p.found && p.found.companyName && <span style={{ fontSize: 12, color: T.muted, fontWeight: 400 }}>found by {methods(p.found.companyName)}</span>}
          <Sources list={p.sources} />
        </Space>
      )}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        <Radio.Group value={d.action} onChange={(e) => set({ action: e.target.value })}>
          <Radio value="create">Add as a new partner</Radio>
          <Radio value="map" disabled={!mapOptions.length}>Map to an existing partner</Radio>
          <Radio value="reject">Reject</Radio>
        </Radio.Group>
        {d.action === "map" && (
          <Select filterOption={matchOption}
            data-testid={`map-${p.id}`}
            style={{ width: "100%", maxWidth: 520 }}
            value={d.companyId}
            placeholder="Choose the partner company"
            options={mapOptions}
            onChange={(v) => set({ companyId: v })}
            showSearch optionFilterProp="search" optionLabelProp="text"
          />
        )}
        {d.action === "map" && best && best.companyId === d.companyId && (
          <div style={{ fontSize: 12, color: T.muted }}>{(best.why || []).join(" · ")}</div>
        )}
        {d.action === "create" && (
          <Input data-testid={`name-${p.id}`} style={{ maxWidth: 520 }} value={d.companyName} placeholder="Company name" onChange={(e) => set({ companyName: e.target.value })} />
        )}
        {d.action !== "reject" && (
          <Select
            mode="tags" style={{ width: "100%", maxWidth: 520 }} value={d.trades} placeholder="Trade / discipline"
            options={(tradeOptions || []).map((t) => ({ value: t, label: t }))}
            onChange={(v) => set({ trades: v })}
          />
        )}
        {(p.notes || []).map((n, i) => <Alert key={i} type="info" showIcon message={n} style={{ fontSize: 12 }} />)}
        {d.action !== "reject" && (
          <Table size="small" rowKey="id" pagination={false} dataSource={d.people} columns={columns} />
        )}
        {d.action !== "reject" && !can.setSupervisors && d.people.some((x) => x.include && x.role !== "employee") && (
          <Alert type="warning" showIcon message="Only the project owner, a company admin or a super admin can set supervisors: supervisors will be added as employees and executives left out. The result will list them." />
        )}
      </Space>
    </Card>
  );
}

function PlanView({ plan }) {
  if (!plan) return null;
  const c = plan.counts || {};
  return (
    <div data-testid="partner-import-plan">
      {plan.refusals && plan.refusals.length > 0 && <Alert type="error" showIcon style={{ marginBottom: 8 }} message="Cannot apply yet" description={plan.refusals.map((r, i) => <div key={i}>{r}</div>)} />}
      {plan.undecided && plan.undecided.length > 0 && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={`Undecided: ${plan.undecided.join(", ")}`} />}
      <p style={{ marginTop: 0 }}>
        {c.partners} partner{c.partners === 1 ? "" : "s"} ({c.newCompanies} new), {c.people} people
        ({c.supervisors} supervisors, {c.executives} executives), {c.skipped} skipped, {c.rejected} rejected. Nobody is emailed;
        the people are added to this project only and arrive muted in Reports &amp; notifications.
      </p>
      {(plan.items || []).filter((i) => i.action !== "reject").map((i) => (
        <div key={i.proposalId} style={{ borderTop: `1px solid ${T.line}`, padding: "6px 0", fontSize: 13 }}>
          <b>{i.companyName}</b> - {i.action === "create" ? "new partner company" : i.row === "add" ? "already on this project: people added" : i.row === "revive" ? "back on this project" : "added to this project"}
          {i.trades && i.trades.length > 0 && <span style={{ color: T.muted }}> · {i.trades.join(", ")}</span>}
          <div style={{ color: T.muted }}>
            {[...i.supervisors.map((s) => `${s.name} (${s.isExecutive ? "executive" : "supervisor"})`), ...i.users.map((u) => `${u.name} (${u.role})`)].join(", ") || "nobody new"}
          </div>
          {i.skipped.map((s, k) => <div key={k} style={{ color: "#b54708", fontSize: 12 }}>Skipped {s.name || s.email}: {s.why}</div>)}
          {i.notes.map((n, k) => <div key={k} style={{ color: "#b54708", fontSize: 12 }}>{n}</div>)}
        </div>
      ))}
    </div>
  );
}

export default function PartnerImportTab({ projectId, reloadKey, onCountChange, onDecided }) {
  const [imports, setImports] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState("");
  const [plan, setPlan] = useState(null);

  const loadList = useCallback(async () => {
    if (!projectId) { setImports([]); return; }
    try {
      const r = await listPartnerImports(projectId, "ready,applied,applying");
      const list = r.imports || [];
      setImports(list);
      if (onCountChange) onCountChange(list.filter((x) => x.status === "ready").length);
      setSelected((cur) => (cur && list.some((x) => x._id === cur) ? cur : ((list.find((x) => x.status === "ready") || list[0] || {})._id || null)));
    } catch (e) {
      setImports([]);
      if (e.status !== 403) message.error(e.message || "Could not read the partner imports");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reloadKey]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return; }
    try {
      const r = await getPartnerImport(projectId, selected);
      setDetail(r);
      const d = {};
      (r.import.proposals || []).forEach((p) => { d[p.id] = initialDecision(p); });
      setDecisions(d);
      setPlan(null);
    } catch (e) { message.error(e.message || "Could not read the import"); }
  }, [projectId, selected]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const run = async (what, fn) => {
    setBusy(what);
    try { return await fn(); } catch (e) {
      if (e.data && e.data.plan) setPlan(e.data.plan);
      message.error(e.message || "Something went wrong");
      return null;
    } finally { setBusy(""); }
  };
  const save = () => run("save", async () => { await savePartnerImportDecisions(projectId, selected, decisions); message.success("Review saved"); });
  const preview = () => run("preview", async () => { const r = await previewPartnerImport(projectId, selected, decisions); setPlan(r.plan); });
  const apply = () => run("apply", async () => {
    const r = await applyPartnerImport(projectId, selected, decisions);
    const c = r.counts || {};
    message.success(`Applied: ${c.partners} partner${c.partners === 1 ? "" : "s"}, ${c.people} people - nobody was emailed`);
    setPlan(null);
    await loadList(); await loadDetail();
    if (onDecided) onDecided();
  });
  const undo = () => Modal.confirm({
    title: "Undo this import?",
    content: "Removes exactly what it added: the people, the rows and companies it created, and their mutes. Anything added by hand since stays.",
    okText: "Undo",
    onOk: () => run("undo", async () => {
      const r = await undoPartnerImport(projectId, selected);
      const u = r.undone || {};
      message.success(`Undone: ${u.removedPeople} people, ${u.removedRows} rows, ${u.removedCompanies} companies removed`);
      [...(u.keptRows || []), ...(u.keptCompanies || [])].forEach((k) => message.info(k));
      await loadList(); await loadDetail();
      if (onDecided) onDecided();
    }),
  });

  if (imports === null) return <Spin />;
  if (!imports.length) return <Empty description="Nothing waiting - import partner documentation from a project's Partners page." />;
  const imp = detail && detail.import;
  const can = (detail && detail.can) || {};
  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          style={{ width: 420 }}
          value={selected}
          onChange={setSelected}
          options={imports.map((x) => ({
            value: x._id,
            label: `${new Date(x.createdAt).toLocaleString()} · ${(x.files || []).length} files · ${x.partners} partners · ${x.status === "ready" ? "waiting for review" : x.status}`,
          }))}
        />
      </Space>
      {!imp ? <Spin /> : (
        <>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
            {(imp.files || []).map((f) => f.name).join(", ")}{imp.createdByName ? ` · imported by ${imp.createdByName}` : ""}
          </div>
          {imp.extraction && imp.extraction.note && <Alert data-testid="partner-import-degraded" type="warning" showIcon style={{ marginBottom: 12 }} message={imp.extraction.note} />}
          {imp.status === "applied" && imp.applied && (
            <Alert
              type="success" showIcon style={{ marginBottom: 12 }}
              message={`Applied${imp.applied.byName ? ` by ${imp.applied.byName}` : ""} - ${(imp.applied.results || []).filter((r) => r.row !== "rejected").length} partners`}
              description={(imp.applied.results || []).filter((r) => r.row !== "rejected").map((r) => (
                <div key={r.proposalId} style={{ fontSize: 12 }}>
                  <b>{r.companyName}</b>: {(r.added || []).map((a) => `${a.name} (${a.as})`).join(", ") || "nobody new"}
                  {(r.skipped || []).length > 0 && <span style={{ color: "#b54708" }}> · skipped {r.skipped.map((s) => `${s.name || s.email} (${s.why})`).join("; ")}</span>}
                  {(r.notes || []).map((n, k) => <div key={k} style={{ color: "#b54708" }}>{n}</div>)}
                </div>
              ))}
              action={can.apply && <Button icon={<RollbackOutlined />} onClick={undo} loading={busy === "undo"}>Undo</Button>}
            />
          )}
          {imp.status === "ready" && (
            <>
              {(imp.proposals || []).map((p) => (
                <ProposalCard
                  key={p.id} p={p} d={decisions[p.id] || initialDecision(p)}
                  onChange={(nd) => setDecisions((cur) => ({ ...cur, [p.id]: nd }))}
                  tradeOptions={imp.tradeOptions} partnerOrgs={detail.partnerOrgs} can={can}
                />
              ))}
              <Space style={{ marginTop: 4 }}>
                {can.apply && <Button icon={<SaveOutlined />} onClick={save} loading={busy === "save"}>Save review</Button>}
                <Button icon={<EyeOutlined />} onClick={preview} loading={busy === "preview"} data-testid="partner-import-preview">Preview</Button>
                {can.apply && <Button type="primary" icon={<CheckOutlined />} onClick={apply} loading={busy === "apply"} data-testid="partner-import-apply">Apply</Button>}
                {!can.apply && <Tag>View only - applying needs participantAdd</Tag>}
              </Space>
              {plan && <div style={{ marginTop: 12 }}><PlanView plan={plan} /></div>}
            </>
          )}
        </>
      )}
    </div>
  );
}
